import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { Menu } from 'components/Menu';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirm } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import {
  filterArrayEmpty,
  arrayJoin,
  stringRemoveNewLines,
  arrayUnique,
} from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import { without } from 'lodash';
import React from 'react';
import { selectRemoteSuggestedActionControl } from 'store/remote/selectors';

type Props = {
  id: number;
  inPositive: boolean;
  inNegative: boolean;
  type: 'action' | 'control';
  onPress: () => void;
  onPressNegative?: () => void;
  onPressPositive?: () => void;
  onPressPositiveNegative?: () => void;
  onDelete: () => void;
  onUnlink: () => void;
  forChecklistId: number;
  forQuestionId: number;
  forAnswerId: number;
};

export const FindingRow: React.FC<Props> = ({
  id,
  type,
  inPositive,
  inNegative,
  onPress,
  onPressNegative,
  onPressPositive,
  onPressPositiveNegative,
  onDelete,
  onUnlink,
  forChecklistId,
  forQuestionId,
  forAnswerId,
}) => {
  const confirm = useConfirm();

  const typeUppercaseLabel = type === 'action' ? 'Action' : 'Control';
  // const allowSeverity = type === 'action';
  // const allowTimescale = type === 'action';
  // const allowCategory = type === 'action' || type === 'control';
  const finding = useSelector((state) =>
    selectRemoteSuggestedActionControl(state, {
      type,
      id,
    }),
  );

  if (!finding) {
    return null;
  }

  const questionsCount = without(finding.questionIds, forQuestionId).length;
  const answersCount = without(finding.answerIds, forAnswerId).length;
  const checklistsCount = arrayUnique(
    without(
      [
        ...finding.negativeChecklistItemIds,
        ...finding.positiveChecklistItemIds,
      ],
      forChecklistId,
    ),
  ).length;

  const otherTextWithCounts = arrayJoin(
    [
      pluralise(
        checklistsCount,
        '1 other checklist item',
        '{x} other checklist items',
        '',
      ),
      pluralise(questionsCount, '1 question', '{x} questions', ''),
      pluralise(answersCount, '1 answer', '{x} answers', ''),
      'the suggestions library',
    ].filter(filterArrayEmpty),
    ', ',
    ' and ',
  );

  const handleUnlink = onUnlink;

  const handleDelete = () => {
    confirm({
      title: `Confirm Delete ${typeUppercaseLabel}`,
      text:
        `Are you sure you want to delete this ${type}?` +
        `\n\nIt will also be deleted from ${otherTextWithCounts}`,
      confirmText: 'Delete',
      destructive: true,
      onConfirm: onDelete,
    });
  };

  const appearButtonStyle = {
    marginTop: -theme.paddingHorizontal,
    marginBottom: -theme.paddingHorizontal,
  };

  return (
    <CardRow
      onPress={onPress}
      menuOptions={[
        {
          text: `Open ${typeUppercaseLabel}`,
          icon: ICONS.CircleInfoLight,
          onPress,
        },
        {
          text: `Unlink ${typeUppercaseLabel}`,
          subText: 'Will still be available in the suggestions library',
          icon: ICONS.LinkHorizontalSlashLight,
          onPress: handleUnlink,
        },
        {
          text: `Delete ${typeUppercaseLabel}`,
          subText: `Will be removed from ${otherTextWithCounts}`,
          icon: ICONS.TrashCanLight,
          destructive: true,
          onPress: handleDelete,
        },
      ]}
    >
      <ViewWithSeparators
        horizontal
        separatorMargin={4}
        style={{
          alignItems: 'center',
          flexDirection: 'row',
        }}
      >
        {(onPressPositive || onPressNegative || onPressPositiveNegative) && (
          <Menu
            width={230}
            iconButtonConfig={{
              size: 30,
              iconSize: 21,
              borderRadius: 4,
              color: 'white',
              ...(inPositive && !inNegative
                ? {
                    icon: ICONS.CheckLight,
                    style: [
                      appearButtonStyle,
                      {
                        backgroundColor: theme.colors.green,
                        borderColor: theme.colors.green,
                      },
                    ],
                    hoverColor: theme.colors.green,
                    pressedColor: theme.colors.green,
                    accessibilityLabel: `${typeUppercaseLabel} appears for positive responses`,
                  }
                : inNegative && !inPositive
                  ? {
                      icon: ICONS.XmarkLight,
                      style: [
                        appearButtonStyle,
                        {
                          backgroundColor: theme.colors.red,
                          borderColor: theme.colors.red,
                        },
                      ],
                      hoverColor: theme.colors.red,
                      pressedColor: theme.colors.red,
                      accessibilityLabel: `${typeUppercaseLabel} appears for negative responses`,
                    }
                  : {
                      icon: ICONS.CircleDashedLight,
                      style: [
                        appearButtonStyle,
                        {
                          backgroundColor: theme.colors.fieldValueText,
                          borderColor: theme.colors.fieldValueText,
                        },
                      ],
                      hoverColor: theme.colors.fieldValueText,
                      pressedColor: theme.colors.fieldValueText,
                      accessibilityLabel: `${typeUppercaseLabel} appears for both positive and negative responses`,
                    }),
            }}
            options={[
              `Suggest this ${type} when answer is...`,
              onPressPositive
                ? {
                    text: 'Positive',
                    textColor: theme.colors.green,
                    icon: ICONS.CheckLight,
                    iconColor: theme.colors.green,
                    onPress: onPressPositive,
                  }
                : undefined,
              onPressNegative
                ? {
                    text: 'Negative',
                    icon: ICONS.XmarkLight,
                    textColor: theme.colors.red,
                    iconColor: theme.colors.red,
                    onPress: onPressNegative,
                  }
                : undefined,
              onPressPositiveNegative
                ? {
                    text: 'Positive or Negative',
                    icon: ICONS.CircleDashedLight,
                    onPress: onPressPositiveNegative,
                  }
                : undefined,
            ].filter(filterArrayEmpty)}
          />
        )}
        <Txt size="small" numberOfLines={2} style={{ flex: 1 }}>
          {stringRemoveNewLines(finding.description)}
          {!!finding.why && (
            <Txt size="small" style={{ color: theme.colors.fieldValueText }}>
              {' '}
              {stringRemoveNewLines(finding.why)}
            </Txt>
          )}
        </Txt>
      </ViewWithSeparators>
    </CardRow>
  );
};
